import { useTranslation } from 'react-i18next';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Edit2, Package } from 'lucide-react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { bookingService } from '@/services/api/booking.service';
import { useQuery } from '@tanstack/react-query';

export default function CustomQuestionsPage() {
  const { t } = useTranslation(['services', 'common']);
  const [, setLocation] = useLocation();

  useSetPageHeader(
    {
      title: t('services:customQuestions.title'),
      sub: t('services:customQuestions.description'),
    },
    [t],
  );

  // Fetch list of services
  const { data: services = [], isLoading } = useQuery({
    queryKey: ['services'],
    queryFn: () => bookingService.getServices(),
  });

  const handleEditQuestions = (serviceId: number) => {
    setLocation(`/admin/services?editService=${serviceId}&expandQuestions=true`);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('services:title')}</CardTitle>
          <CardDescription>
            Select a service to configure its custom booking questions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-soft">
              {t('common:loading')}
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-12 text-soft">
              <Package className="h-12 w-12 mx-auto mb-4 text-soft" />
              <p className="text-lg font-medium">{t('services:list.empty')}</p>
              <p className="text-sm mt-2">{t('services:list.emptyDescription')}</p>
              <Button
                className="mt-4"
                onClick={() => setLocation('/admin/services')}
              >
                {t('services:create.button')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {services.map((service) => {
                const questionCount = service.custom_booking_questions?.length ||
                                     service.customBookingQuestions?.length || 0;

                return (
                  <div
                    key={service.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-surface-2 cursor-pointer transition-colors"
                    onClick={() => handleEditQuestions(service.id)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-h3 text-ink">{service.name}</h3>
                        {!service.active && (
                          <Badge tone="neutral">
                            {t('common:status.inactive')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-soft mt-1">
                        {questionCount === 0 ? (
                          <span className="text-soft">{t('services:customQuestions.empty')}</span>
                        ) : (
                          <span>
                            {questionCount} question{questionCount !== 1 ? 's' : ''} configured
                          </span>
                        )}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="min-h-[44px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditQuestions(service.id);
                      }}
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit Questions
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 p-4 bg-brand-50 border border-brand-200 rounded-lg">
        <h3 className="font-medium text-brand-900 mb-1">{t('services:customQuestions.quickTip')}</h3>
        <p className="text-sm text-brand-800">
          Custom questions are now configured directly in the service form. Click on any service
          above to edit its details and configure custom booking questions in the same flow.
        </p>
      </div>
    </div>
  );
}
